<script setup lang="ts">
const { t } = useL10n()
const route = useRoute()
const user = useUserStore()
const { sidebarOpen } = useAppShell()

const isMobile = useMediaQuery('(max-width: 900px)')
const showFeedback = ref(false)

// The drawer sits above the page on mobile; leaving it open behind the dialog
// would trap the user between two overlays.
const openFeedback = () => {
  sidebarOpen.value = false
  showFeedback.value = true
}

const navItems = computed(() => [
  {
    key: 'dashboard',
    icon: 'pi pi-gauge',
    label: t('layout.nav.dashboard'),
    to: '/dashboard'
  },
  {
    key: 'projects',
    icon: 'pi pi-briefcase',
    label: t('pages.projects.title'),
    to: '/projects'
  },
  {
    key: 'appointments',
    icon: 'pi pi-calendar',
    label: t('pages.appointments.title'),
    to: '/appointments'
  },
  {
    key: 'invoices',
    icon: 'pi pi-receipt',
    label: t('pages.invoices.title'),
    to: '/invoices'
  },
  {
    key: 'myProjects',
    icon: 'ti-project',
    label: t('pages.myProjects.title'),
    to: '/my-projects',
    children: [
      {
        key: 'myProjectsNew',
        icon: 'pi pi-plus',
        label: t('pages.myProjects.create'),
        to: '/my-projects/new'
      }
    ]
  }
])

// The dashboard is only itself; every other entry also owns its detail routes.
const isActive = (to: string) =>
  to === '/dashboard' ? route.path === to : route.path.startsWith(to)

// A child is a single screen, so it only lights up on its own route — otherwise
// "Neues Projekt" would stay marked while the list below it is open.
const isChildActive = (to: string) => route.path === to

watch(
  () => route.path,
  () => {
    sidebarOpen.value = false
  }
)

watch(isMobile, (mobile) => {
  if (!mobile) sidebarOpen.value = false
})
</script>

<template>
  <Transition name="backdrop-fade">
    <div
      v-if="user.isLoggedInAsContact && isMobile && sidebarOpen"
      class="sidebar-backdrop"
      @click="sidebarOpen = false"
    />
  </Transition>

  <aside
    v-if="user.isLoggedInAsContact"
    class="nav-sidebar"
    :class="{ isMobile, isOpen: sidebarOpen }"
  >
    <!-- The drawer covers the header on mobile, so it carries its own brand
         row and a close button. -->
    <div v-if="isMobile" class="drawer-header">
      <img src="/Logo.png" alt="Die Leckorter" class="drawer-logo" />
      <button
        type="button"
        class="drawer-close"
        :aria-label="t('layout.nav.close')"
        @click="sidebarOpen = false"
      >
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>

    <nav class="sidebar-nav" :aria-label="t('layout.nav.label')">
      <template v-for="item in navItems" :key="item.key">
        <NuxtLink :to="item.to" class="sidebar-link" :class="{ isActive: isActive(item.to) }">
          <i :class="item.icon" aria-hidden="true"></i>
          <span>{{ item.label }}</span>
        </NuxtLink>

        <NuxtLink
          v-for="child in item.children || []"
          :key="child.key"
          :to="child.to"
          class="sidebar-link isChild"
          :class="{ isActive: isChildActive(child.to) }"
        >
          <i :class="child.icon" aria-hidden="true"></i>
          <span>{{ child.label }}</span>
        </NuxtLink>
      </template>
    </nav>

    <div class="sidebar-footer">
      <button type="button" class="sidebar-link" @click="openFeedback">
        <i class="pi pi-comment" aria-hidden="true"></i>
        <span>{{ t('layout.nav.feedback') }}</span>
      </button>
    </div>
  </aside>

  <FeedbackDialog v-model:visible="showFeedback" />
</template>

<style scoped lang="scss">
.sidebar-backdrop {
  position: fixed;
  inset: 0;
  background: rgb(0 0 0 / 40%);
  z-index: 90;
}

.nav-sidebar {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: var(--portal-sidebar-width);
  background: var(--portal-card);
  border-right: 1px solid var(--portal-border);

  // Desktop: stays put while the page scrolls underneath the sticky header.
  position: sticky;
  top: var(--portal-header-height);
  align-self: flex-start;
  height: calc(100dvh - var(--portal-header-height));

  // Mobile: off-canvas drawer over the full viewport height. `align-self` has
  // to be reset — Chrome applies the flex container's alignment to a fixed
  // child, which collapses the drawer to its content height despite top/bottom.
  &.isMobile {
    position: fixed;
    top: 0;
    left: 0;
    align-self: auto;
    height: 100dvh;
    width: 16rem;
    z-index: 100;
    transform: translateX(-100%);
    transition: transform 0.2s ease;

    &.isOpen {
      transform: translateX(0);
    }
  }
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: var(--portal-header-height);
  // Left padding compensates the logo's own dark margin, so the wordmark lines
  // up with the nav icons underneath it.
  padding: 0 0.75rem 0 0.375rem;
  overflow: hidden;
  background: var(--portal-brand-plate);
}

.drawer-logo {
  height: 3.25rem;
  width: auto;
  object-fit: contain;
}

.drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  margin-left: auto;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: rgb(255 255 255 / 75%);
  cursor: pointer;

  &:hover {
    background: rgb(255 255 255 / 12%);
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -2px;
  }
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  padding: 0.75rem 0.5rem;
  overflow-y: auto;
}

.sidebar-footer {
  padding: 0.5rem;
  border-top: 1px solid var(--portal-border);
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--portal-muted);
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  i {
    font-size: 1rem;
    flex-shrink: 0;
  }

  span {
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
  }

  &:hover {
    background: var(--portal-bg);
    color: var(--portal-text);
  }

  &:focus-visible {
    outline: 2px solid var(--portal-primary);
    outline-offset: -2px;
  }

  &.isActive {
    background: var(--portal-primary-soft);
    color: var(--portal-primary-strong);
  }

  // Nested entry: indented under its parent and a touch quieter, so the group
  // reads as one block rather than three peers.
  &.isChild {
    padding-left: 2.25rem;
    font-size: 0.8125rem;

    i {
      font-size: 0.875rem;
    }
  }
}

.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
  transition: opacity 0.2s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .nav-sidebar.isMobile,
  .backdrop-fade-enter-active,
  .backdrop-fade-leave-active {
    transition: none;
  }
}
</style>
